import { useState, useEffect, useMemo, useCallback } from "react";
import {
  Modal,
  ModalHeader,
  ModalBody,
  Table,
  Input,
  Button,
  Collapse,
} from "reactstrap";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import Select from "react-select";
import makeAnimated from "react-select/animated";
import { FormattedMessage } from "react-intl";
import { GetFinalExamVariantsForCourse } from "../../actions/teacherQueries";
import {
  DeleteFinalExamVariant,
  ChangeFinalExamVariant,
} from "../../actions/teacherMutations";
import { toast } from "react-toastify";
import { useMutation } from "react-query";
import { useQueryClient } from "react-query";
import axios from "../../services/api/api";
import PromptGeneric from "../Modal/PromptGeneric";
import { injectIntl } from "react-intl";
import {
  faChevronRight,
  faChevronUp,
  faUser,
} from "@fortawesome/free-solid-svg-icons";
import Pacmanloader from "../../components/Pacmanloader/Pacmanloader";
import StatusBadge from "../Badges/StatusBadge";
import moment from "moment";
import { TOAST_CONFIG } from "../../Utilities/toastConfig";
import { PiPianoKeys } from "react-icons/pi";
import { QAPI_URL } from "../../constants/api.constants";

const ShowFinalExamModal = ({
  classId,
  courseId,
  courseName,
  student,
  level,
  isOpen,
  toggle,
  intl,
}) => {
  const mutateDeleteExamVariant = useMutation(DeleteFinalExamVariant);
  const mutateChangeFinalExamVariant = useMutation(ChangeFinalExamVariant);

  const animatedComponents = makeAnimated();
  const queryClient = useQueryClient();

  // display handling
  const [examDetailCollapsed, setExamDetailCollapsed] = useState(true);
  const [selectedExam, setSelectedExam] = useState(null);
  const [nextSelectedExam, setNextSelectedExam] = useState(null);
  const [studentInAccesses, setStudentInAccesses] = useState([]);
  const [rawStudentOptions, setRawStudentOptions] = useState([]);
  const [studentsStartedExam, setStudentsStartedExam] = useState([]);

  // change handling
  const [selectedStudents, setSelectedStudents] = useState([]);
  const [studentIdsRemoved, setStudentIdsRemoved] = useState([]);
  const [studentIdsCanRun, setStudentIdsCanRun] = useState([]);
  const [studentIdsCanNotRun, setStudentIdsCanNotRun] = useState([]);
  const [changesUnsavedPrompt, setChangesUnsavedPrompt] = useState(false);
  const [deleteExamPrompt, setDeleteExamPrompt] = useState(false);
  const [allStudentsCanRun, setAllStudentsCanRun] = useState(false);

  const finalExamVariants = GetFinalExamVariantsForCourse(classId, courseId);

  const finalExamVariantOptions = useMemo(
    () =>
      finalExamVariants?.data?.length > 0
        ? finalExamVariants.data
            .sort((a, b) => new Date(b.created) - new Date(a.created))
            .map((exam) => ({
              label:
                exam.name +
                " - (" +
                moment(exam.created).format("YYYY-MM-DD") +
                ")",
              value: exam.id,
            }))
        : [],
    [finalExamVariants.data]
  );

  const getFinalExamVariantById = useCallback(async (id) => {
    try {
      const res = await axios.get(`/Course/GetFinalExamVariant?id=${id}`);
      return res.data.result;
    } catch (err) {
      return null;
    }
  }, []);

  const restructStudentsData = useCallback(
    (student) => ({
      id: student.student?.id || student.id,
      label: student.student?.name || student.label || student.name,
      class:
        student.student?.class?.name || student.class?.name || student.class,
      canRun: student.canRun ?? false,
    }),
    []
  );

  const studentOptions = useMemo(() => {
    const excludedIds = studentInAccesses.map((s) => s.id);

    const filteredOptions = rawStudentOptions.filter(
      (s) =>
        !excludedIds.includes(s.id) &&
        !selectedStudents.some((sel) => sel.id === s.id)
    );

    const options = filteredOptions.sort((a, b) =>
      a.label.localeCompare(b.label)
    );

    if (level === "Student") return filteredOptions;

    return [
      {
        id: 0,
        label: intl.formatMessage({ id: "SelectAll" }),
      },
      ...options,
    ];
  }, [rawStudentOptions, studentInAccesses, level, intl, selectedStudents]);

  const handleExamVariantContents = useCallback(
    async (select) => {
      if (!select || !select.value) return;

      const data = await getFinalExamVariantById(select.value);
      if (!data) return;
      const exam = data.finalExamVariant || {};
      const accesses = data.finalExamVariant.accesses || [];
      const studentsToAdd = data.studentsPossibleToAddFromClass || [];

      setSelectedExam(exam);

      const restructStudentsToAdd = studentsToAdd.map((s) =>
        restructStudentsData(s)
      );
      setRawStudentOptions(
        level === "Student"
          ? restructStudentsToAdd.filter((s) => s.id === student[0].id)
          : restructStudentsToAdd
      );

      const accessesNotStarted = accesses.filter((s) => !s.isStarted);
      const restructAccessesNotStarted = accessesNotStarted.map((s) =>
        restructStudentsData(s)
      );
      setStudentInAccesses(
        level === "Student"
          ? restructAccessesNotStarted.filter((s) => s.id === student[0].id)
          : restructAccessesNotStarted
      );

      const accessesInProgress = accesses.filter((s) => s.isStarted);
      setStudentsStartedExam(accessesInProgress);

      setNextSelectedExam(null);
    },
    [
      getFinalExamVariantById,
      restructStudentsData,
      level,
      student,
      setSelectedExam,
      setRawStudentOptions,
      setStudentInAccesses,
      setStudentsStartedExam,
      setNextSelectedExam,
    ]
  );

  const handleExamVariantSelect = useCallback(
    (select) => {
      if (!select || !select.value) return;

      if (
        (studentIdsRemoved.length > 0 ||
          studentIdsCanRun.length > 0 ||
          studentIdsCanNotRun.length > 0) &&
        select.value !== selectedExam?.id
      ) {
        setChangesUnsavedPrompt(true);
        setNextSelectedExam(select);
      } else {
        handleExamVariantContents(select);
      }
    },
    [
      studentIdsRemoved,
      studentIdsCanRun,
      studentIdsCanNotRun,
      selectedExam,
      handleExamVariantContents,
    ]
  );

  const updateIds = (prev, ids, allCanRun) =>
    allCanRun
      ? [...new Set([...prev, ...ids])]
      : prev.filter((id) => !ids.has(id));

  const handleLockUnlockAllStudents = (event) => {
    if (studentInAccesses.length === 0) return;

    const allCanRun = event.target.checked;
    setAllStudentsCanRun(allCanRun);

    const idsInAccess = new Set(
      studentInAccesses.map((s) => s.student?.id || s.id)
    );

    setStudentInAccesses((prev) =>
      prev.map((s) => ({ ...s, canRun: allCanRun }))
    );

    setStudentIdsCanRun((prev) => updateIds(prev, idsInAccess, allCanRun));
    setStudentIdsCanNotRun((prev) => updateIds(prev, idsInAccess, !allCanRun));
  };

  const handleLockUnlockStudentCanRunChange = (access) => {
    const id = access.student?.id || access.id;

    const canRun = !access.canRun;

    setStudentInAccesses((prev) =>
      prev.map((s) => (s.id === access.id ? { ...s, canRun } : s))
    );

    if (canRun) {
      setStudentIdsCanRun((prev) => [...new Set([...prev, id])]);
      setStudentIdsCanNotRun((prev) => prev.filter((i) => i !== id));
    } else {
      setStudentIdsCanRun((prev) => prev.filter((i) => i !== id));
      setStudentIdsCanNotRun((prev) => [...new Set([...prev, id])]);
    }
  };

  const handleRemoveStudent = (selectedStudent) => {
    setStudentInAccesses((prev) =>
      prev.filter((s) => s.id !== selectedStudent.id)
    );
    setRawStudentOptions((prev) => {
      const alreadyExists = prev.some((s) => s.id === selectedStudent.id);
      return alreadyExists ? prev : [...prev, selectedStudent];
    });
    setStudentIdsRemoved((prev) => [...new Set([...prev, selectedStudent.id])]);
    setStudentIdsCanNotRun((prev) =>
      prev.filter((id) => id !== selectedStudent.id)
    );
    setStudentIdsCanRun((prev) =>
      prev.filter((id) => id !== selectedStudent.id)
    );
  };

  const mappedStudentsStartedExam = () => {
    return studentsStartedExam && studentsStartedExam.length > 0 ? (
      studentsStartedExam.map((access, i) => (
        <tr key={i}>
          <td style={{ width: "50px" }}></td>
          <td className="align-middle text-start" style={{ width: "180px" }}>
            {access.student.name}
          </td>
          <td className="align-middle text-start" style={{ width: "100px" }}>
            {access.student.class.name}
          </td>
          <td className="align-middle text-center">
            <StatusBadge
              className="px-1"
              status={access.status}
              isGy25FinalExam={true}
            />
          </td>
        </tr>
      ))
    ) : (
      <tr>
        <td colSpan={4} className="text-center py-3 text-muted">
          <FormattedMessage id="NoStudentsFound" />
        </td>
      </tr>
    );
  };

  const mappedStudentsInAccess = () => {
    return studentInAccesses.length > 0 ? (
      studentInAccesses?.map((access, i) => (
        <tr key={i}>
          <td>
            <Button
              outline
              color="danger"
              onClick={() => handleRemoveStudent(access)}
              style={{ cursor: "pointer" }}
              className="border-0"
            >
              <FontAwesomeIcon icon="trash" />
            </Button>
          </td>
          <td>{access.student?.name || access.label}</td>
          <td>{access.student?.class?.name || access.class}</td>
          <td>
            <Button
              className="status-toggle"
              color="light"
              size="sm"
              onClick={() => handleLockUnlockStudentCanRunChange(access)}
            >
              {access.canRun ? (
                <span style={{ width: "18px" }}>
                  <FontAwesomeIcon
                    icon="lock-open"
                    className="text-primary"
                    size="lg"
                  />
                </span>
              ) : (
                <span style={{ width: "18px" }}>
                  <FontAwesomeIcon
                    icon="lock"
                    className="text-muted"
                    size="lg"
                  />
                </span>
              )}
            </Button>
          </td>
          <td></td>
        </tr>
      ))
    ) : (
      <tr>
        <td colSpan={4} className="text-center py-3 text-muted">
          <FormattedMessage id="NoStudentsFound" />
        </td>
      </tr>
    );
  };

  const handleAddStudents = (selectedStudents) => {
    if (!selectedExam || !selectedStudents) return;

    const addedIds = selectedStudents.map((s) => s.id);

    setStudentInAccesses((prev) => [...prev, ...selectedStudents]);
    setStudentIdsRemoved((prev) => prev.filter((id) => !addedIds.includes(id)));
    setStudentIdsCanNotRun((prev) => [...new Set([...prev, ...addedIds])]);
    setSelectedStudents([]);
  };

  const handleSelectChange = (selectedOptions) => {
    const isSelectAll = selectedOptions.find((opt) => opt.id === 0);

    let options = isSelectAll
      ? studentOptions.filter((opt) => opt.id !== 0)
      : selectedOptions;

    setSelectedStudents(options);
  };

  const handleCancelPrompt = async () => {
    setChangesUnsavedPrompt(false);
    if (nextSelectedExam) {
      setSelectedExam(nextSelectedExam);
      setNextSelectedExam(null);
      await handleExamVariantContents(nextSelectedExam);

      setStudentIdsRemoved([]);
      setStudentIdsCanRun([]);
      setStudentIdsCanNotRun([]);
    }
  };

  const handleSaveChangesAndSetNextSelectedExam = async (exam) => {
    handleSaveChangesForExamVariant(exam);
    if (nextSelectedExam) {
      await handleExamVariantContents(nextSelectedExam);
    }
  };

  const handleSaveChangesForExamVariant = (exam) => {
    let values = {
      id: exam.id,
      name: exam.name,
      studentIdsCanRun: studentIdsCanRun,
      studentIdsCanNotRun: studentIdsCanNotRun,
      studentIdsRemoved: studentIdsRemoved,
    };
    mutateChangeFinalExamVariant.mutate(values, {
      onSuccess: async (data) => {
        if (data?.status > 199 && data.status < 300) {
          toast.success(
            <FormattedMessage id="ExamVariantUpdated" />,
            TOAST_CONFIG
          );
          setStudentIdsRemoved([]);
          setStudentIdsCanRun([]);
          setStudentIdsCanNotRun([]);
        } else {
          toast.warn(data ? data : "");
        }
      },
      onError: (data) => {
        toast.warn(data.response.data);
      },
    });
    setChangesUnsavedPrompt(false);
  };

  const handleDeleteExamVariant = (exam) => {
    mutateDeleteExamVariant.mutate(exam.id, {
      onSuccess: (data) => {
        if (data?.status > 199 && data.status < 300) {
          toast.success(
            <FormattedMessage id="ExamVariantDeleted" />,
            TOAST_CONFIG
          );

          queryClient.invalidateQueries([
            "FetchFinalExamVariantsForCourse",
            classId,
            courseId,
          ]);

          setSelectedExam(null);
        } else {
          toast.warn(data ? data : "");
        }
      },
      onError: (data) => {
        toast.warn(data.response.data);
      },
    });
    setDeleteExamPrompt(false);
  };

  const handleOpenFinaExamVariantAnswers = () => {
    axios
      .post("/Course/GetFinalExamAnswers", {
        finalExamVariantId: selectedExam.id,
      })
      .then((response) => {
        if (response.data?.result) {
          const data = JSON.parse(response.data.result);
          const win = window.open(
            `${QAPI_URL}/RT/RT?mode=5&lng=sv`,
            "",
            `fullscreen=yes,width=${window.screen.width},height=${window.screen.height}`
          );
          setTimeout(() => {
            win.postMessage({ fun: "init", param: { Data: data } }, "*");
          }, 2000);
        }
      })
      .catch((err) => {
        toast.warn(err?.message);
      });
  };

  const totalNumQuestions = selectedExam?.content?.reduce(
    (sum, c) => sum + (c.questionCount || 0),
    0
  );

  useEffect(() => {
    if (!isOpen) {
      setSelectedExam(null);
      setRawStudentOptions([]);
      setStudentInAccesses([]);
      setSelectedStudents([]);
      setStudentIdsRemoved([]);
      setStudentIdsCanRun([]);
      setStudentIdsCanNotRun([]);
    }
  }, [isOpen]);

  useEffect(() => {
    if (isOpen && studentInAccesses.length > 0) {
      const allCanRun = studentInAccesses.every((s) => s.canRun === true);
      setAllStudentsCanRun(allCanRun);
    } else if (studentInAccesses.length === 0) {
      setAllStudentsCanRun(false);
    }
  }, [isOpen, studentInAccesses]);

  if (finalExamVariants.isLoading) return <Pacmanloader />;

  return (
    <Modal isOpen={isOpen} toggle={toggle} modalClassName="custom-modal">
      <ModalHeader toggle={toggle}>
        <div className="ms-2">
          <FormattedMessage id="Edit" /> - {courseName}
        </div>
      </ModalHeader>

      <ModalBody className="m-4">
        <div className="d-flex align-items-center mb-3">
          <label className="form-label fw-bold me-4">
            <FormattedMessage id="Exam" />
          </label>
          <Select
            className="w-100"
            classNamePrefix="custom-select"
            closeMenuOnSelect
            components={animatedComponents}
            name="finalExamVariants"
            options={finalExamVariantOptions}
            onChange={handleExamVariantSelect}
            value={
              selectedExam
                ? {
                    label: selectedExam?.name,
                    value: selectedExam?.id,
                  }
                : null
            }
            placeholder={<FormattedMessage id="SelectExam" />}
          />
        </div>

        {selectedExam && (
          <>
            <div className="py-3 mb-3 align-items-center border-bottom">
              <div className="d-flex justify-content-between align-items-center mb-3">
                <div
                  className="fw-bold"
                  style={{ cursor: "pointer" }}
                  onClick={() => setExamDetailCollapsed(!examDetailCollapsed)}
                >
                  <FontAwesomeIcon
                    className={
                      examDetailCollapsed
                        ? "fa-caret--animated me-2"
                        : "fa-caret--animated me-2 collapsed"
                    }
                    icon={examDetailCollapsed ? faChevronRight : faChevronUp}
                    style={{ fontSize: "12px" }}
                  />
                  <FormattedMessage id="ExamOverview" />
                </div>
                <Button
                  outline
                  size="sm"
                  className="d-flex align-items-center"
                  color={"primary"}
                  style={{ cursor: "pointer" }}
                  onClick={handleOpenFinaExamVariantAnswers}
                >
                  <PiPianoKeys size={14} className="me-1" />
                  <FormattedMessage id="Answers" />
                </Button>
              </div>
              <Collapse isOpen={!examDetailCollapsed}>
                <div style={{ maxHeight: "350px", overflowY: "auto" }}>
                  <Table borderless size="sm" className="exam-table mt-3">
                    <thead className="table-light">
                      <tr>
                        <th
                          colSpan="4"
                          className="text-center fw-bold py-2 border-bottom text-info"
                        >
                          <FormattedMessage id="ExamDetails" />
                        </th>
                      </tr>
                      <tr>
                        <th style={{ width: "10%" }}></th>
                        <th
                          className="fw-medium text-start"
                          style={{ width: "50%" }}
                        >
                          <FormattedMessage id="Module" /> (
                          {selectedExam?.content?.length})
                        </th>
                        <th
                          className="fw-medium text-end"
                          style={{ width: "30%" }}
                        >
                          <FormattedMessage id="QuestionCount" /> (
                          {totalNumQuestions})
                        </th>
                        <th style={{ width: "10%" }}></th>
                      </tr>
                    </thead>

                    <tbody>
                      {selectedExam?.content &&
                        selectedExam?.content.map((item, i) => (
                          <tr key={i}>
                            <td style={{ width: "10%" }}></td>
                            <td
                              className="align-middle text-start"
                              style={{ width: "50%" }}
                            >
                              {item.module.nameCorrespondingFactModule}
                            </td>
                            <td
                              className="align-middle text-end"
                              style={{ width: "30%" }}
                            >
                              {item.questionCount}
                            </td>
                            <td style={{ width: "10%" }}></td>
                          </tr>
                        ))}
                    </tbody>
                  </Table>

                  <Table borderless size="sm" className="exam-table mt-3">
                    <thead className="table-light">
                      <tr>
                        <th
                          colSpan="4"
                          className="text-center fw-bold py-2 border-bottom text-info"
                        >
                          <FormattedMessage id="StudentsStartedExam" />
                        </th>
                      </tr>

                      <tr>
                        <th style={{ width: "45px" }}></th>
                        <th
                          className="fw-medium text-start"
                          style={{ width: "180px" }}
                        >
                          <FormattedMessage id="Student" />
                        </th>
                        <th
                          className="fw-medium text-start"
                          style={{ width: "100px" }}
                        >
                          <FormattedMessage id="Class" />
                        </th>
                        <th className="text-center fw-medium">
                          <FormattedMessage id="Status" />
                        </th>
                      </tr>
                    </thead>

                    <tbody>{mappedStudentsStartedExam()}</tbody>
                  </Table>
                </div>
              </Collapse>
            </div>

            <div className="d-flex justify-content-between align-items-center pt-3 mb-3 ">
              <div className="d-flex align-items-center">
                <FontAwesomeIcon
                  icon={faUser}
                  className="me-2"
                  style={{ fontSize: 12 }}
                />
                <div className="fw-bold">
                  <FormattedMessage id="EditStudents" />
                </div>
              </div>

              <div className="d-flex align-items-center">
                <div style={{ marginRight: "28px" }}>
                  {allStudentsCanRun ? (
                    <FormattedMessage id="LockAll" />
                  ) : (
                    <FormattedMessage id="UnlockAll" />
                  )}
                </div>

                <div
                  className="form-check form-switch"
                  style={{ marginBottom: "-1px", marginRight: "-10px" }}
                  id={`tooltip-switch-${selectedExam?.id}`}
                  data-bs-toggle="tooltip"
                  data-bs-placement="left"
                  title={
                    allStudentsCanRun
                      ? intl.formatMessage({
                          id: "LockAllStudents",
                        })
                      : intl.formatMessage({
                          id: "UnlockAllStudents",
                        })
                  }
                >
                  <Input
                    type="switch"
                    className="rounded"
                    style={{ cursor: "pointer" }}
                    id={`activate_all_switch_${selectedExam?.id}`}
                    name={`activate_all_switch${selectedExam?.id}`}
                    checked={allStudentsCanRun}
                    onChange={(event) => handleLockUnlockAllStudents(event)}
                  />
                </div>
              </div>
            </div>

            <div className="exam-table-container">
              <div className="exam-students-head-wrapper">
                <Table
                  borderless
                  hover
                  size="sm"
                  className="exam-students-head"
                >
                  <thead className="table-light">
                    <tr>
                      <th></th>
                      <th>
                        <FormattedMessage id="Student" />
                      </th>
                      <th>
                        <FormattedMessage id="Class" />
                      </th>
                      <th>
                        <FormattedMessage id="Runnable" />
                      </th>
                      <th></th>
                    </tr>
                  </thead>
                </Table>
              </div>

              <div className="exam-students-body-wrapper">
                <Table
                  borderless
                  hover
                  size="sm"
                  className="exam-students-body"
                >
                  <tbody>{mappedStudentsInAccess()}</tbody>
                </Table>
              </div>
            </div>

            <div className="d-flex flex-row align-items-center justify-content-between mt-3">
              <Select
                className="w-10 flex-grow-1 me-2"
                classNamePrefix="custom-select"
                closeMenuOnSelect={false}
                components={animatedComponents}
                isMulti
                name="students"
                getOptionValue={(option) => option.id}
                getOptionLabel={(option) => option.label}
                options={studentOptions}
                isOptionSelected={(option) =>
                  selectedStudents.some((s) => s.id === option.id)
                }
                onChange={handleSelectChange}
                value={selectedStudents}
                placeholder={
                  <div className="ms-2">
                    <FormattedMessage id="ChooseStudents" />
                  </div>
                }
              />
              <Button
                onClick={() => handleAddStudents(selectedStudents)}
                disabled={selectedStudents.length === 0}
                color="info"
                outline
              >
                <FontAwesomeIcon icon="plus" />
              </Button>
            </div>
          </>
        )}

        <div className="d-flex flex-row justify-content-end mt-4">
          <Button
            type="button"
            outline
            color="secondary"
            onClick={toggle}
            className="px-3 py-1 me-2"
          >
            <FormattedMessage id="Cancel" />
          </Button>

          <Button
            color="outline-danger"
            onClick={() => setDeleteExamPrompt(true)}
            className="px-3 py-1 me-2"
            disabled={!selectedExam}
          >
            <FormattedMessage id="Delete" />
          </Button>

          <Button
            type="submit"
            color="primary"
            outline
            onClick={() => handleSaveChangesForExamVariant(selectedExam)}
            className="px-3 py-1"
            disabled={
              !selectedExam ||
              (studentIdsCanNotRun.length === 0 &&
                studentIdsCanRun.length === 0 &&
                studentIdsRemoved.length === 0)
            }
          >
            <FormattedMessage id="Save" />
          </Button>
        </div>
      </ModalBody>

      <PromptGeneric
        onProceed={() => handleDeleteExamVariant(selectedExam)}
        toggle={() => setDeleteExamPrompt(!deleteExamPrompt)}
        isOpen={deleteExamPrompt}
        title={<FormattedMessage id="AreYouSure" />}
        text={<FormattedMessage id="SureYouWantDeleteFinalExam" />}
        btnText="Delete"
        danger={true}
      />
      <PromptGeneric
        onProceed={() => handleSaveChangesAndSetNextSelectedExam(selectedExam)}
        toggle={() => handleCancelPrompt()}
        isOpen={changesUnsavedPrompt}
        title={<FormattedMessage id="AreYouSure" />}
        text={<FormattedMessage id="UnsavedChangsWarning" />}
        btnText="Save"
      />
    </Modal>
  );
};

export default injectIntl(ShowFinalExamModal);
